// src/components/shared/CourseCard/variants/SuggestionCard.tsx
import { parseGenEdEntry, type ParsedGenEdEntry } from '@/utils/genEdEntry';
import React, { useState, type ChangeEvent, type MouseEvent } from 'react';
import { Sparkles, Check, X, Loader2, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';
import CourseAvailabilityBadge from '../../CourseAvailabilityBadge';
import CourseRequisites from '../components/CourseRequisites';
import GradeDistributionChart from '../../GradeDistributionChart';
import { getRoleColors, getRequirementPillColors } from '../utils';
import type { CheckAvailabilityFn } from '../hooks/useCourseCard';
import { useCreditUnit, useInstitutionConfig } from '../../../../contexts/InstitutionConfigContext';
import { useInterestMatch } from '../../../../hooks/useInterestMatch';

interface GradeData {
  distribution?: Record<string, number>;
  total_students?: number;
  average_gpa?: number;
  difficulty?: 'easy' | 'moderate' | 'challenging' | 'hard' | string;
}

export interface SuggestionCourse {
  code: string;
  title?: string;
  course_title?: string;
  credits?: number;
  description?: string;
  gen_ed_requirements?: string[];
  /** Resolved OR-group picks (from gen_ed_override); drives the OR select. */
  gen_ed_resolved?: string[];
  genEdArea?: string;
  genEdAlternatives?: string[];
  requirementKey?: string;
  grade_data?: GradeData;
  /** Recorded causal reason for the suggestion (Phase 6b) */
  reason?: string | null;
  /** Catalog requisite text, verbatim (shown in the expanded view). */
  prerequisites_raw?: string | null;
  corequisites_raw?: string | null;
  restriction?: string | null;
  /** Phase 2.2: consent/permission gate the plan assumes is granted. */
  assumes_approval?: 'consent' | 'restricted-entry' | string | null;
  [key: string]: unknown;
}

export interface SuggestionCardProps {
  course: SuggestionCourse;
  semester?: string;
  courseRoles?: string[];
  onAcceptSuggestion?: (courseCode: string, semester: string) => Promise<void> | void;
  onRejectSuggestion?: (courseCode: string, semester: string) => Promise<void> | void;
  onReassignGenEd?: (courseCode: string, area: string) => void;
  isAcceptingSuggestion?: boolean;
  isRejectingSuggestion?: boolean;
  checkCourseAvailability?: CheckAvailabilityFn;
}

/**
 * SuggestionCard — Ghost card for optimizer suggestions. Not draggable; users
 * must accept or reject suggestions.
 */
const SuggestionCard = ({
  course,
  semester,
  courseRoles,
  onAcceptSuggestion,
  onRejectSuggestion,
  onReassignGenEd,
  isAcceptingSuggestion = false,
  isRejectingSuggestion = false,
  checkCourseAvailability,
}: SuggestionCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Gate grade-related UI on institution flag. The existing optional
  // chaining is the safety net for missing per-course data; this flag
  // hides the UI entirely for institutions with no grade-data source.
  const { config: institutionConfig } = useInstitutionConfig();
  const creditUnit = useCreditUnit();
  const showGradeUI = institutionConfig.hasGradeData;
  // Prior-stack Phase 4E: surface WHY this course was suggested when it matches
  // one of the student's interest tags.
  const matchFor = useInterestMatch();
  const interestMatch = matchFor(course.code);

  const handleAccept = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (onAcceptSuggestion && !isAcceptingSuggestion && semester) {
      onAcceptSuggestion(course.code, semester);
    }
  };

  const handleReject = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (onRejectSuggestion && !isRejectingSuggestion && semester) {
      onRejectSuggestion(course.code, semester);
    }
  };

  const handleCardClick = (e: MouseEvent<HTMLDivElement>): void => {
    const target = e.target as Element;
    if (target.closest('button') || target.closest('select')) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const isLoading = isAcceptingSuggestion || isRejectingSuggestion;
  const title = course.title || course.course_title || '';
  const hasGenEd = !!course.gen_ed_requirements && course.gen_ed_requirements.length > 0;
  const contextLabel = course.genEdArea || course.requirementKey;
  const hasAlternatives = !!course.genEdAlternatives && course.genEdAlternatives.length > 1;

  const handleAreaChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    e.stopPropagation();
    if (onReassignGenEd) {
      onReassignGenEd(course.code, e.target.value);
    }
  };

  const pillColors = getRequirementPillColors(course, courseRoles);

  const genEdEntries: ParsedGenEdEntry[] = hasGenEd
    ? (course.gen_ed_requirements ?? []).map(parseGenEdEntry)
    : [];

  const hasLine2Content =
    !!course.credits || hasGenEd || hasAlternatives || !!contextLabel || !!interestMatch;

  const semesterValue = semester;

  return (
    <div
      aria-label={`Suggestion: ${course.code} - ${title}`}
      aria-expanded={isExpanded}
      onClick={handleCardClick}
      className={`
        relative group rounded-lg cursor-pointer
        bg-status-suggested/10 border-2 border-dashed border-status-suggested/50
        transition-all duration-200 ease-out
        hover:border-status-suggested/70 hover:bg-status-suggested/15 hover:shadow-sm
        ${isExpanded ? 'ring-2 ring-status-suggested/25' : ''}
        ${isLoading ? 'opacity-60 pointer-events-none' : ''}
      `}
      style={{ padding: '0.25rem 0.5rem' }}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-r overflow-hidden z-10 flex flex-col">
        {(getRoleColors(courseRoles) || ['bg-status-suggested/60']).map((color, i, arr) => (
          <div
            key={i}
            className={color}
            style={{
              flex: 1,
              borderRadius: i === 0 && arr.length === 1 ? '0 0.25rem 0.25rem 0' : undefined,
            }}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 min-w-0 ml-2 h-[28px]">
        <Sparkles className="w-3 h-3 text-status-suggested flex-shrink-0" />

        <span className="font-semibold text-status-suggested-fg text-sm whitespace-nowrap">
          {course.code}
        </span>

        <span className="text-ink-3/60 text-xs">•</span>

        <span className="text-xs text-ink-2 truncate flex-1" title={title}>
          {title}
        </span>

        <div className="flex-shrink-0 ml-1">
          {isExpanded ? (
            <ChevronDown className="w-3 h-3 text-ink-3" />
          ) : (
            <ChevronRight className="w-3 h-3 text-ink-3" />
          )}
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
          <button
            onClick={handleAccept}
            disabled={isLoading}
            className="p-1 rounded hover:bg-success/15 text-success transition-colors disabled:opacity-50"
            title="Add to plan"
          >
            {isAcceptingSuggestion ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            onClick={handleReject}
            disabled={isLoading}
            className="p-1 rounded hover:bg-status-critical/15 text-status-critical transition-colors disabled:opacity-50"
            title="Dismiss suggestion"
          >
            {isRejectingSuggestion ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <X className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>

      {hasLine2Content && (
        <div className="flex flex-wrap items-center gap-2 ml-2 min-h-[22px] mt-0.5">
          <span className="text-xs text-ink-2 whitespace-nowrap flex-shrink-0">
            {creditUnit.format(course.credits)}
          </span>

          {showGradeUI && course.grade_data?.distribution && (
            <div
              className="flex h-2 w-8 rounded-sm overflow-hidden flex-shrink-0"
              title={`Avg GPA ${course.grade_data.average_gpa?.toFixed(2) || '?'} • ${course.grade_data.total_students?.toLocaleString() || '?'} students`}
            >
              {[
                { key: 'A', color: 'bg-green-500' },
                { key: 'B', color: 'bg-yellow-400' },
                { key: 'C', color: 'bg-orange-400' },
                { key: 'D', color: 'bg-orange-600' },
                { key: 'F', color: 'bg-red-500' },
                { key: 'W', color: 'bg-gray-400' },
              ].map((g) => {
                const pct = course.grade_data?.distribution?.[g.key];
                return pct && pct > 0 ? (
                  <div key={g.key} className={g.color} style={{ width: `${pct}%` }} />
                ) : null;
              })}
            </div>
          )}

          {(hasGenEd || hasAlternatives || contextLabel) && (
            <span className="text-ink-3/60 text-xs">|</span>
          )}

          {genEdEntries.map((entry, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span className="text-ink-3/60 text-xs">,</span>}
              {entry.type === 'or' && entry.areas ? (
                <select
                  // Controlled by the resolved choice (gen_ed_resolved, from
                  // the suggestion's/enrollment's override) so the select shows
                  // the ACTUAL pick, and wired to onReassignGenEd — mirrors
                  // UltraCompactCard. This select used to have a defaultValue
                  // and no onChange: it rendered but did nothing at any
                  // institution (2026-09-24 gen-ed generality audit).
                  value={
                    (course.gen_ed_resolved ?? []).find((a) => entry.areas?.includes(a)) ??
                    entry.areas[0]
                  }
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    onReassignGenEd?.(course.code, e.target.value);
                  }}
                  disabled={!onReassignGenEd}
                  // Width cap matches UltraCompactCard's gen-ed controls so a
                  // long area code can't push past the card edge (`truncate` is
                  // best-effort on <select> across browsers — the cap is what's
                  // load-bearing; `title` keeps the full text reachable).
                  className="px-1 py-0 bg-green-50 text-green-700 text-[10px] rounded border border-green-200 cursor-pointer hover:bg-green-100 focus:outline-none focus:ring-1 focus:ring-green-400 disabled:cursor-default max-w-[8rem] truncate"
                  title={`Choose: ${entry.areas.join(' or ')}`}
                >
                  {entry.areas.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              ) : (
                // `truncate` supplies white-space: nowrap, so the previous
                // standalone `whitespace-nowrap` (which could neither wrap nor
                // clip) is folded into it. `title` keeps the full code readable.
                <span
                  className="text-[10px] text-green-700 max-w-[8rem] truncate"
                  title={entry.area}
                >
                  {entry.area}
                </span>
              )}
            </React.Fragment>
          ))}

          {!hasGenEd && hasAlternatives ? (
            <select
              value={course.genEdArea || ''}
              onChange={handleAreaChange}
              onClick={(e) => e.stopPropagation()}
              // Same guard as the OR select above — this renders the same
              // gen-ed area codes and had the same unbounded width.
              className={`px-1 py-0 ${pillColors} text-[10px] rounded border cursor-pointer focus:outline-none max-w-[8rem] truncate`}
              title="Choose gen-ed area"
            >
              {(course.genEdAlternatives ?? []).map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
          ) : !hasGenEd && contextLabel ? (
            <span
              className={`px-1.5 py-0.5 ${pillColors} text-[10px] rounded border truncate max-w-[120px]`}
              title={contextLabel}
            >
              {contextLabel}
            </span>
          ) : null}

          {semesterValue &&
            semesterValue !== 'bookmarks' &&
            semesterValue !== '000008' &&
            checkCourseAvailability && (
              <CourseAvailabilityBadge
                courseCode={course.code}
                targetSemester={semesterValue}
                checkAvailability={checkCourseAvailability as never}
                compact={true}
              />
            )}

          {interestMatch && (
            <span
              className="ml-auto flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-primary/10 text-primary-strong border border-primary/20 whitespace-nowrap max-w-[160px]"
              title={`Matches your interest: ${interestMatch}`}
            >
              <Sparkles className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="truncate">matches: {interestMatch}</span>
            </span>
          )}

          {course.assumes_approval && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-status-warning/10 text-status-warning-fg border border-status-warning/20 whitespace-nowrap"
              title="This course typically requires instructor/department approval (audition, consent, or restricted entry). The plan assumes it is granted — confirm with the department."
            >
              <ShieldAlert className="w-2.5 h-2.5 flex-shrink-0" />
              <span>approval needed</span>
            </span>
          )}
        </div>
      )}

      {isExpanded && (
        <div className="px-3 pb-3 pt-2 ml-2 border-t border-status-suggested/25 mt-2">
          <div className="text-sm text-ink font-medium mb-2">{title}</div>

          {course.description && (
            <p className="text-xs text-ink-2 mb-2">{course.description}</p>
          )}

          <CourseRequisites
            prerequisites={course.prerequisites_raw}
            corequisites={course.corequisites_raw}
            restriction={course.restriction}
          />

          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {course.reason && (
              <>
                <div className="text-ink-2">Why suggested:</div>
                <div className="text-ink">{course.reason}</div>
              </>
            )}

            <div className="text-ink-2">{creditUnit.heading}:</div>
            <div className="text-ink">{course.credits}</div>

            {hasGenEd && (
              <>
                <div className="text-ink-2">Gen Ed:</div>
                <div className="text-green-700">
                  {(course.gen_ed_requirements ?? []).join(', ')}
                </div>
              </>
            )}

            {showGradeUI && course.grade_data?.difficulty && (
              <>
                <div className="text-ink-2">Difficulty:</div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                      course.grade_data.difficulty === 'easy'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : course.grade_data.difficulty === 'moderate'
                          ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                          : course.grade_data.difficulty === 'challenging'
                            ? 'bg-orange-50 text-orange-700 border-orange-200'
                            : 'bg-red-50 text-red-700 border-red-200'
                    }`}
                  >
                    {course.grade_data.difficulty.charAt(0).toUpperCase() +
                      course.grade_data.difficulty.slice(1)}
                  </span>
                  {course.grade_data.average_gpa && (
                    <span className="text-gray-600 text-[10px]">
                      Avg GPA: {course.grade_data.average_gpa.toFixed(2)}
                    </span>
                  )}
                  {course.grade_data.total_students && (
                    <span className="text-gray-400 text-[10px]">
                      ({course.grade_data.total_students.toLocaleString()} students)
                    </span>
                  )}
                </div>
              </>
            )}

            {showGradeUI && course.grade_data?.distribution && (
              <>
                <div className="text-ink-2">Grades:</div>
                <div className="col-span-1">
                  <GradeDistributionChart
                    distribution={course.grade_data.distribution}
                    totalStudents={course.grade_data.total_students}
                    averageGpa={course.grade_data.average_gpa}
                    variant="compact"
                  />
                </div>
              </>
            )}

            {semesterValue &&
              semesterValue !== 'bookmarks' &&
              semesterValue !== '000008' &&
              checkCourseAvailability && (
                <>
                  <div className="text-ink-2">Availability:</div>
                  <div>
                    <CourseAvailabilityBadge
                      courseCode={course.code}
                      targetSemester={semesterValue}
                      checkAvailability={checkCourseAvailability as never}
                      compact={false}
                      showWhenAvailable={true}
                    />
                  </div>
                </>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestionCard;
